/**
 * ServiceAccountsTab Component
 *
 * Manages OAuth2 M2M service accounts for the current tenant.
 * Allows creating, updating status, rotating secrets, and deleting.
 */

import { useState } from 'react';
import { useI18n } from '@/i18n';
import {
  Button,
  Badge,
  Skeleton,
  Icon,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui';
import { LabeledInput } from '@/components/ui/form-wrappers';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Key, Copy, RefreshCw, Trash2, PlusCircle, AlertTriangle } from 'lucide-react';
import {
  useServiceAccounts,
  useCreateServiceAccount,
  useUpdateServiceAccount,
  useDeleteServiceAccount,
  useRotateServiceAccountSecret,
  ALL_SCOPES,
  type ServiceAccount,
  type ServiceAccountCreated,
  type RotatedSecret,
  type AllowedScope,
} from '@/hooks/use-service-accounts';

// =============================================================================
// Helpers
// =============================================================================

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'active') return 'default';
  if (status === 'suspended') return 'secondary';
  return 'destructive';
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`${label} copied to clipboard`);
  });
}

// =============================================================================
// Secret Reveal Box (one-time disclosure)
// =============================================================================

interface SecretRevealProps {
  clientId: string;
  clientSecret: string;
  onClose: () => void;
}

function SecretReveal({ clientId, clientSecret, onClose }: SecretRevealProps) {
  return (
    <div className="space-y-4 py-2">
      <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 dark:text-amber-300">
          <strong>Store these credentials now.</strong> The secret will never be shown again.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Client ID
        </label>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 text-sm font-mono break-all">
            {clientId}
          </code>
          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(clientId, 'Client ID')}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Client Secret
        </label>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 text-sm font-mono break-all">
            {clientSecret}
          </code>
          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(clientSecret, 'Client Secret')}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={onClose}>Done — I've saved the credentials</Button>
      </DialogFooter>
    </div>
  );
}

// =============================================================================
// Create Service Account Dialog
// =============================================================================

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateServiceAccountDialog({ open, onOpenChange }: CreateDialogProps) {
  const { t } = useI18n();
  const createMutation = useCreateServiceAccount();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopes, setScopes] = useState<AllowedScope[]>([]);
  const [created, setCreated] = useState<ServiceAccountCreated | null>(null);

  const toggleScope = (scope: AllowedScope) => {
    setScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  const handleSubmit = async () => {
    if (!name.trim() || scopes.length === 0) return;
    try {
      const result = await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        scopes,
      });
      setCreated(result);
    } catch {
      toast.error(t('admin.serviceAccounts.createError') || 'Failed to create service account');
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setScopes([]);
    setCreated(null);
    onOpenChange(false);
  };

  // Group scopes by category
  const scopeGroups = ALL_SCOPES.reduce<Record<string, typeof ALL_SCOPES>>((acc, s) => {
    if (!acc[s.group]) acc[s.group] = [];
    acc[s.group].push(s);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            {created
              ? (t('admin.serviceAccounts.createdTitle') || 'Service Account Created')
              : (t('admin.serviceAccounts.createTitle') || 'New Service Account')}
          </DialogTitle>
        </DialogHeader>

        {created ? (
          <SecretReveal
            clientId={created.client_id}
            clientSecret={created.client_secret}
            onClose={handleClose}
          />
        ) : (
          <>
            <div className="space-y-4 py-4">
              <LabeledInput
                label={t('admin.serviceAccounts.name') || 'Name'}
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder="e.g. CI/CD pipeline"
                required
              />
              <LabeledInput
                label={t('admin.serviceAccounts.description') || 'Description (optional)'}
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                placeholder="Optional description"
              />

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {t('admin.serviceAccounts.scopes') || 'Scopes'} <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3 max-h-56 overflow-y-auto rounded border border-gray-200 dark:border-gray-700 p-3">
                  {Object.entries(scopeGroups).map(([group, groupScopes]) => (
                    <div key={group}>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        {group}
                      </p>
                      <div className="space-y-1 pl-1">
                        {groupScopes.map(s => (
                          <label key={s.value} className="flex items-center gap-2 cursor-pointer text-sm">
                            <Checkbox
                              checked={scopes.includes(s.value)}
                              onCheckedChange={() => toggleScope(s.value)}
                            />
                            <span className="text-gray-800 dark:text-gray-200">{s.label}</span>
                            <code className="text-xs text-gray-400 font-mono">{s.value}</code>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {scopes.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Select at least one scope</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!name.trim() || scopes.length === 0 || createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <><Icon name="loader" size={16} className="animate-spin mr-1" /> Creating...</>
                ) : (
                  t('admin.serviceAccounts.create') || 'Create'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Rotate Secret Dialog
// =============================================================================

interface RotateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sa: ServiceAccount;
}

function RotateSecretDialog({ open, onOpenChange, sa }: RotateDialogProps) {
  const { t } = useI18n();
  const rotateMutation = useRotateServiceAccountSecret(sa.id);
  const [rotated, setRotated] = useState<RotatedSecret | null>(null);

  const handleRotate = async () => {
    try {
      const result = await rotateMutation.mutateAsync();
      setRotated(result);
    } catch {
      toast.error(t('admin.serviceAccounts.rotateError') || 'Failed to rotate secret');
    }
  };

  const handleClose = () => {
    setRotated(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            {t('admin.serviceAccounts.rotateTitle') || 'Rotate Secret'}
          </DialogTitle>
        </DialogHeader>

        {rotated ? (
          <SecretReveal
            clientId={rotated.client_id}
            clientSecret={rotated.client_secret}
            onClose={handleClose}
          />
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 py-2">
              {t('admin.serviceAccounts.rotateWarning', { name: sa.name }) ||
                `Rotating the secret for "${sa.name}" will immediately invalidate the current secret. Any running integrations using the old secret will stop working.`}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                variant="destructive"
                onClick={handleRotate}
                disabled={rotateMutation.isPending}
              >
                {rotateMutation.isPending ? (
                  <><Icon name="loader" size={16} className="animate-spin mr-1" /> Rotating...</>
                ) : (
                  t('admin.serviceAccounts.rotate') || 'Rotate Secret'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Service Account Row
// =============================================================================

interface SARowProps {
  sa: ServiceAccount;
  onRotate: (sa: ServiceAccount) => void;
  onDelete: (sa: ServiceAccount) => void;
}

function ServiceAccountRow({ sa, onRotate, onDelete }: SARowProps) {
  const { t } = useI18n();
  const updateMutation = useUpdateServiceAccount(sa.id);

  const toggleStatus = async () => {
    const newStatus = sa.status === 'active' ? 'suspended' : 'active';
    try {
      await updateMutation.mutateAsync({ status: newStatus });
      toast.success(
        newStatus === 'active'
          ? (t('admin.serviceAccounts.activated') || 'Service account activated')
          : (t('admin.serviceAccounts.suspended') || 'Service account suspended')
      );
    } catch {
      toast.error(t('admin.serviceAccounts.updateError') || 'Failed to update status');
    }
  };

  return (
    <div className="flex items-start justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-start gap-3 min-w-0">
        <Key className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-gray-100">{sa.name}</span>
            <Badge variant={statusVariant(sa.status)} className="text-xs capitalize">
              {sa.status}
            </Badge>
          </div>
          {sa.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sa.description}</p>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {sa.scopes.map(scope => (
              <code key={scope} className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono text-gray-600 dark:text-gray-300">
                {scope}
              </code>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1 font-mono">{sa.client_id}</p>
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0 ml-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleStatus}
          disabled={updateMutation.isPending || sa.status === 'revoked'}
          title={sa.status === 'active' ? 'Suspend' : 'Activate'}
        >
          {updateMutation.isPending ? (
            <Icon name="loader" size={14} className="animate-spin" />
          ) : sa.status === 'active' ? (
            <Icon name="pause" size={14} />
          ) : (
            <Icon name="play" size={14} />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onRotate(sa)} disabled={sa.status === 'revoked'} title="Rotate secret">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          onClick={() => onDelete(sa)}
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Delete Confirm Dialog
// =============================================================================

interface DeleteDialogProps {
  sa: ServiceAccount | null;
  onClose: () => void;
}

function DeleteConfirmDialog({ sa, onClose }: DeleteDialogProps) {
  const { t } = useI18n();
  const deleteMutation = useDeleteServiceAccount();

  if (!sa) return null;

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(sa.id);
      toast.success(t('admin.serviceAccounts.deleteSuccess') || 'Service account deleted');
      onClose();
    } catch {
      toast.error(t('admin.serviceAccounts.deleteError') || 'Failed to delete service account');
    }
  };

  return (
    <Dialog open={!!sa} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            {t('admin.serviceAccounts.deleteTitle') || 'Delete Service Account'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600 dark:text-gray-400 py-2">
          {t('admin.serviceAccounts.deleteWarning', { name: sa.name }) ||
            `Delete "${sa.name}"? This immediately revokes all access for integrations using this client.`}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? (
              <><Icon name="loader" size={16} className="animate-spin mr-1" /> Deleting...</>
            ) : (
              t('common.delete') || 'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// ServiceAccountsTab
// =============================================================================

export function ServiceAccountsTab() {
  const { t } = useI18n();
  const { data, isLoading, error } = useServiceAccounts();
  const [showCreate, setShowCreate] = useState(false);
  const [rotatingSA, setRotatingSA] = useState<ServiceAccount | null>(null);
  const [deletingSA, setDeletingSA] = useState<ServiceAccount | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {t('admin.serviceAccounts.loadError') || 'Failed to load service accounts'}
      </div>
    );
  }

  const accounts = data?.data || [];

  return (
    <>
      <div className="space-y-3">
        {accounts.length === 0 ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <Key className="mx-auto h-8 w-8 mb-2 text-gray-400" />
            <p className="font-medium">
              {t('admin.serviceAccounts.empty') || 'No service accounts yet'}
            </p>
            <p className="text-sm mt-1">
              {t('admin.serviceAccounts.emptyHint') ||
                'Service accounts provide OAuth2 M2M credentials for CI/CD pipelines and integrations.'}
            </p>
            <Button className="mt-4" onClick={() => setShowCreate(true)}>
              <PlusCircle className="h-4 w-4 mr-1.5" />
              {t('admin.serviceAccounts.createFirst') || 'Create Service Account'}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <PlusCircle className="h-4 w-4 mr-1.5" />
                {t('admin.serviceAccounts.create') || 'New Service Account'}
              </Button>
            </div>
            {accounts.map(sa => (
              <ServiceAccountRow
                key={sa.id}
                sa={sa}
                onRotate={setRotatingSA}
                onDelete={setDeletingSA}
              />
            ))}
          </>
        )}
      </div>

      <CreateServiceAccountDialog open={showCreate} onOpenChange={setShowCreate} />

      {rotatingSA && (
        <RotateSecretDialog
          open={!!rotatingSA}
          onOpenChange={open => { if (!open) setRotatingSA(null); }}
          sa={rotatingSA}
        />
      )}

      <DeleteConfirmDialog
        sa={deletingSA}
        onClose={() => setDeletingSA(null)}
      />
    </>
  );
}

export default ServiceAccountsTab;
